import {
  Button,
  IconButton,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { updateUserContributorProfileSchemaFactory } from '@/schemas/contributors/update-user-contributor-profile';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useMemo, useState, useRef } from 'react';
import type { ChangeEvent } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { useNotifications } from '@/hooks/use-notifications';
import usePatchUserContributorMutation from '@/state/mutations/contributors/use-patch-user-contributor';
import useUploadContributorProfilePhotoMutation from '@/state/mutations/contributors/use-upload-contributor-profile-photo';
import ContributorProfileInputs from './contributor-profile.inputs';
import ProfileImagePreview from './profile-image-preview';
import { ProfilePhotoAvatar } from '@/components/common/profile-photo-avatar';
import { useGetContributorProfilePhotoUrl } from '@/state/queries/contributors/use-get-contributor-profile-photo-url';

type ViewMode = 'stored' | 'comparison' | 'input';

const panelSx = {
  flex: 1,
  minWidth: 0,
  borderRadius: { xs: 1.5, sm: 2 },
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
};

const panelHeaderSx = {
  px: { xs: 1, sm: 2 },
  py: { xs: 0.75, sm: 1.5 },
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'action.hover',
};

const ContributorProfileForm = ({
  contributor,
  alwaysExpanded = false,
  onClose,
}: {
  contributor: ContributorSummaryDto | ContributorDto;
  alwaysExpanded?: boolean;
  onClose?: () => void;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [isEditing, setIsEditing] = useState(() => alwaysExpanded || !contributor.userContributor?.providedProfile);
  const [viewMode, setViewMode] = useState<ViewMode>('comparison');
  const [successButton, setSuccessButton] = useState<'saveDraft' | 'submit' | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isStoredPreviewOpen, setIsStoredPreviewOpen] = useState(false);
  const [isUserInputPreviewOpen, setIsUserInputPreviewOpen] = useState(false);
  const [uploadedPhotoUri, setUploadedPhotoUri] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: patchUserContributorMutation, isPending: isPatchUserContributorPending } =
    usePatchUserContributorMutation();
  const { mutateAsync: uploadProfilePhotoMutation } = useUploadContributorProfilePhotoMutation();

  const contributorProfile = useMemo(() => contributor.profiles[contributor.profiles.length - 1], [contributor]);
  const userContributorProfile = contributor.userContributor?.providedProfile;

  const contributorPhotoUri =
    (contributorProfile && 'photoUri' in contributorProfile && typeof contributorProfile.photoUri === 'string'
      ? contributorProfile.photoUri
      : '') || '';
  const contributorAssetName = contributorProfile?.assetName;
  const contributorId = contributor.id;

  const { data: contributorPhotoUrl } = useGetContributorProfilePhotoUrl(
    contributorAssetName ? contributorId : undefined,
    contributorAssetName,
  );
  const userContributorPhotoUri = uploadedPhotoUri || userContributorProfile?.photoUri || '';

  const displayName = useMemo(() => {
    return userContributorProfile?.displayName || contributorProfile?.displayName || '';
  }, [contributorProfile, userContributorProfile]);

  const updateUserContributorProfileSchema = updateUserContributorProfileSchemaFactory(t);
  type ContributorProfileFormType = z.infer<typeof updateUserContributorProfileSchema>;

  const savedStateRef = useRef<ContributorProfileFormType | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    control: editableFormControl,
    handleSubmit,
    reset: resetEditableForm,
    getValues,
    formState: { errors },
  } = useForm<ContributorProfileFormType>({
    resolver: zodResolver(updateUserContributorProfileSchema),
    defaultValues: {
      displayName: '',
      title: '',
      organization: '',
      bio: '',
      contactEmail: '',
      contactPhone: '',
    },
  });

  const getInitialSavedState = (): ContributorProfileFormType => {
    const userContributorProfile = contributor.userContributor?.providedProfile;
    return {
      displayName: userContributorProfile?.displayName || contributorProfile.displayName || '',
      title: userContributorProfile?.title || contributorProfile.title || '',
      organization: userContributorProfile?.organization || contributorProfile.organization || '',
      bio: userContributorProfile?.bio || contributorProfile.bio || '',
      contactEmail: userContributorProfile?.contactEmail || contributor.email || '',
      contactPhone: userContributorProfile?.contactPhone || contributor.phoneNumber || '',
    };
  };

  useEffect(() => {
    const initialState = getInitialSavedState();
    savedStateRef.current = initialState;
    resetEditableForm(initialState);
  }, [contributor, resetEditableForm, contributorProfile]);

  useEffect(() => {
    if (userContributorProfile?.photoUri && uploadedPhotoUri) {
      const propUriBase = userContributorProfile.photoUri.split('?')[0];
      const uploadedUriBase = uploadedPhotoUri.split('?')[0];
      if (propUriBase === uploadedUriBase) {
        setUploadedPhotoUri(null);
      }
    }
  }, [userContributorProfile?.photoUri, uploadedPhotoUri]);

  const currentValues = useWatch({ control: editableFormControl }) ?? {};

  const hasChanges = useMemo(() => {
    if (!savedStateRef.current) return false;

    const saved = savedStateRef.current;
    const current = currentValues;

    return (
      saved.displayName !== (current.displayName || '') ||
      saved.title !== (current.title || '') ||
      saved.organization !== (current.organization || '') ||
      saved.bio !== (current.bio || '') ||
      saved.contactEmail !== (current.contactEmail || '') ||
      saved.contactPhone !== (current.contactPhone || '')
    );
  }, [currentValues]);

  const hasBeenSubmitted = useMemo(() => {
    return !!contributor.userContributor?.providedProfile?.submittedAt;
  }, [contributor.userContributor?.providedProfile?.submittedAt]);

  const hasChangesAfterSubmission = useMemo(() => {
    const providedProfile = contributor.userContributor?.providedProfile;
    if (providedProfile?.submittedVersion === undefined || providedProfile?.version === undefined) {
      return false;
    }
    return providedProfile.version > providedProfile.submittedVersion;
  }, [
    contributor.userContributor?.providedProfile?.version,
    contributor.userContributor?.providedProfile?.submittedVersion,
  ]);

  const shouldDisableSubmitBase = useMemo(() => {
    return !hasChanges && hasBeenSubmitted && !hasChangesAfterSubmission;
  }, [hasChanges, hasBeenSubmitted, hasChangesAfterSubmission]);

  const { control: viewOnlyFormControl, reset: resetViewOnlyForm } = useForm<ContributorProfileFormType>({
    resolver: zodResolver(updateUserContributorProfileSchema),
    defaultValues: {
      displayName: '',
      title: '',
      organization: '',
      bio: '',
      contactEmail: '',
      contactPhone: '',
    },
  });

  useEffect(() => {
    resetViewOnlyForm({
      displayName: contributorProfile.displayName || '',
      title: contributorProfile.title || '',
      organization: contributorProfile.organization || '',
      bio: contributorProfile.bio || '',
    });
  }, [contributorProfile, resetViewOnlyForm]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onSubmit = async (data: ContributorProfileFormType) => {
    if (!contributor.userContributor?.id) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      await patchUserContributorMutation({
        userContributorId: contributor.userContributor?.id,
        providedProfile: {
          ...data,
          submittedAt: new Date(),
        },
      });
      savedStateRef.current = data;
      resetEditableForm(data);
      setSuccessButton('submit');
      if (!alwaysExpanded) {
        timeoutRef.current = setTimeout(() => {
          setIsEditing(false);
          setSuccessButton(null);
          timeoutRef.current = null;
        }, 1000);
      } else {
        timeoutRef.current = setTimeout(() => {
          setSuccessButton(null);
          timeoutRef.current = null;
        }, 1000);
      }
    } catch {
      notification.notifyError(t('user.notification.profileUpdateError'));
    }
  };

  const onSaveDraft = async () => {
    if (!contributor.userContributor?.id) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const draftData = getValues();
    try {
      await patchUserContributorMutation({
        userContributorId: contributor.userContributor?.id,
        providedProfile: draftData,
      });
      savedStateRef.current = draftData;
      resetEditableForm(draftData);
      setSuccessButton('saveDraft');
      if (!alwaysExpanded) {
        timeoutRef.current = setTimeout(() => {
          setIsEditing(false);
          setSuccessButton(null);
          timeoutRef.current = null;
        }, 1000);
      } else {
        timeoutRef.current = setTimeout(() => {
          setSuccessButton(null);
          timeoutRef.current = null;
        }, 1000);
      }
    } catch {
      notification.notifyError(t('user.notification.profileUpdateError'));
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (savedStateRef.current) {
      resetEditableForm(savedStateRef.current);
    } else {
      const initialState = getInitialSavedState();
      resetEditableForm(initialState);
    }
    if (!alwaysExpanded) {
      setIsEditing(false);
    }
  };

  const handlePhotoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !contributor.userContributor?.id) return;

    setIsUploadingPhoto(true);
    try {
      const result = await uploadProfilePhotoMutation({
        userContributorId: contributor.userContributor.id,
        file,
      });
      if (result?.photoUri) {
        setUploadedPhotoUri(result.photoUri);
      }
      notification.notifySuccess(
        t('user.notification.profilePhotoUploadSuccess') || 'Profile photo uploaded successfully',
      );
      if (event.target) {
        event.target.value = '';
      }
    } catch {
      notification.notifyError(t('user.notification.profilePhotoUploadError') || 'Failed to upload profile photo');
      if (event.target) {
        event.target.value = '';
      }
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const showStored = viewMode === 'stored' || viewMode === 'comparison';
  const showInput = viewMode === 'input' || viewMode === 'comparison';

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ p: { xs: 0.5, sm: 2 }, height: '100%', overflow: 'auto' }}
    >
      {isEditing && (
        <Stack spacing={{ xs: 1, sm: 2 }}>
          {/* View mode toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  'px': { xs: 1, sm: 2 },
                  'py': 0.5,
                  'textTransform': 'none',
                  'fontSize': { xs: '0.75rem', sm: '0.8125rem' },
                  'borderColor': 'divider',
                  '&.Mui-selected': {
                    'backgroundColor': 'secondary.main',
                    'color': 'secondary.contrastText',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="stored">{t('myEventsList.contributorProfileForm.viewMode.stored')}</ToggleButton>
              <ToggleButton value="comparison">
                {t('myEventsList.contributorProfileForm.viewMode.comparison')}
              </ToggleButton>
              <ToggleButton value="input">{t('myEventsList.contributorProfileForm.viewMode.input')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Panels */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: viewMode === 'comparison' ? 'row' : 'column',
            }}
          >
            {/* Stored on Event panel */}
            {showStored && (
              <Box sx={{ ...panelSx, width: '100%' }}>
                <Box sx={panelHeaderSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {t('myEventsList.contributorProfileForm.storedInEvent')}
                  </Typography>
                </Box>
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  {/* Stored photo */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                    {contributorAssetName ? (
                      <ProfilePhotoAvatar
                        contributorId={contributorId}
                        assetName={contributorAssetName}
                        displayName={contributorProfile?.displayName}
                        onClick={() => contributorAssetName && setIsStoredPreviewOpen(true)}
                        sx={{
                          'width': { xs: 56, sm: 80 },
                          'height': { xs: 70, sm: 100 },
                          'bgcolor': 'primary.main',
                          'border': '1px solid',
                          'borderColor': 'divider',
                          'borderRadius': 1.5,
                          'cursor': contributorAssetName ? 'pointer' : 'default',
                          '& img': { objectFit: 'cover' },
                        }}
                      />
                    ) : (
                      <Avatar
                        key={contributorPhotoUri || 'no-photo'}
                        src={contributorPhotoUri || undefined}
                        alt={contributorProfile?.displayName || 'Profile'}
                        onClick={() => contributorPhotoUri && setIsStoredPreviewOpen(true)}
                        sx={{
                          'width': { xs: 56, sm: 80 },
                          'height': { xs: 70, sm: 100 },
                          'bgcolor': 'primary.main',
                          'border': '1px solid',
                          'borderColor': 'divider',
                          'borderRadius': 1.5,
                          'cursor': contributorPhotoUri ? 'pointer' : 'default',
                          '& img': { objectFit: 'cover' },
                        }}
                      >
                        {contributorProfile?.displayName ? contributorProfile.displayName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    )}
                  </Box>
                  <ContributorProfileInputs control={viewOnlyFormControl} errors={errors} />
                </Box>
              </Box>
            )}

            {/* Your Input panel */}
            {showInput && (
              <Box sx={{ ...panelSx, width: '100%' }}>
                <Box sx={panelHeaderSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {t('myEventsList.contributorProfileForm.yourInput')}
                  </Typography>
                </Box>
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  {/* User input photo with upload */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        key={userContributorPhotoUri}
                        src={userContributorPhotoUri}
                        alt={displayName || 'Profile'}
                        onClick={() => userContributorPhotoUri && setIsUserInputPreviewOpen(true)}
                        sx={{
                          'width': { xs: 56, sm: 80 },
                          'height': { xs: 70, sm: 100 },
                          'bgcolor': 'primary.main',
                          'border': '1px solid',
                          'borderColor': 'divider',
                          'borderRadius': 1.5,
                          'cursor': userContributorPhotoUri ? 'pointer' : 'default',
                          '& img': { objectFit: 'cover' },
                        }}
                      >
                        {displayName ? displayName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      {isUploadingPhoto ? (
                        <CircularProgress
                          size={18}
                          sx={{ position: 'absolute', bottom: 2, right: 2, color: 'primary.main' }}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          sx={{
                            'position': 'absolute',
                            'bottom': -6,
                            'right': -6,
                            'bgcolor': 'background.paper',
                            'border': '1px solid',
                            'borderColor': 'divider',
                            'width': 26,
                            'height': 26,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/*"
                        style={{ display: 'none' }}
                      />
                    </Box>
                  </Box>
                  <ContributorProfileInputs isEditable control={editableFormControl} errors={errors} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Action buttons */}
          {viewMode !== 'stored' && (
            <Stack direction="row" gap={1} sx={{ justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={handleCancel}
                disabled={!onClose && (!hasChanges || isPatchUserContributorPending)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant={successButton === 'saveDraft' ? 'contained' : 'outlined'}
                color={successButton === 'saveDraft' ? 'success' : 'info'}
                size="small"
                onClick={onSaveDraft}
                loading={isPatchUserContributorPending}
                disabled={!hasChanges && successButton !== 'saveDraft'}
                startIcon={successButton === 'saveDraft' ? <CheckCircleIcon /> : undefined}
                sx={{
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  ...(successButton === 'saveDraft' && {
                    'backgroundColor': 'success.main',
                    'color': 'success.contrastText',
                    '&:hover': { backgroundColor: 'success.dark' },
                  }),
                }}
              >
                {t('common.save')} {t('common.draft')}
              </Button>
              <Button
                variant="contained"
                color={successButton === 'submit' ? 'success' : 'info'}
                size="small"
                type="submit"
                loading={isPatchUserContributorPending}
                disabled={shouldDisableSubmitBase && successButton !== 'submit'}
                startIcon={successButton === 'submit' ? <CheckCircleIcon /> : undefined}
                sx={{
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  ...(successButton === 'submit' && {
                    'backgroundColor': 'success.main',
                    'color': 'success.contrastText',
                    '&:hover': { backgroundColor: 'success.dark' },
                  }),
                }}
              >
                {t('common.submit')}
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <ProfileImagePreview
        open={isStoredPreviewOpen}
        onClose={() => setIsStoredPreviewOpen(false)}
        imageUrl={contributorPhotoUrl || contributorPhotoUri || undefined}
        alt={contributorProfile?.displayName || 'Profile'}
      />

      <ProfileImagePreview
        open={isUserInputPreviewOpen}
        onClose={() => setIsUserInputPreviewOpen(false)}
        imageUrl={userContributorPhotoUri}
        alt={displayName || 'Profile'}
      />
    </Stack>
  );
};

export default ContributorProfileForm;
